/**
 * הקמת חברה — onboarding, within purchased capacity. OA-0001 §3.11, 02 §5.2, D2.
 *
 * THREE FACTS THIS FORM IS BUILT AROUND:
 *
 *   · A NEW COMPANY STARTS `prospect`, NOT `active`. D2's billable count reads `lifecycle_status`,
 *     so creating a row must not silently create a charge. The form says so, because an office that
 *     expects a new company to be immediately workable will otherwise report it as a bug.
 *   · CAPACITY IS CHECKED BEFORE CREATION, by the server. If the office is at its limit the refusal
 *     carries the count, the limit and a route — and it appears here, before the form, rather than
 *     after the office has typed a legal name.
 *   · `entityKind` IS DECLARED, NEVER SNIFFED. 02 §5.2's six kinds are a choice a person makes;
 *     an עוסק מורשה and a חברה בע״מ are not distinguishable from a name, and guessing one is how a
 *     company acquires a legal form nobody chose.
 *
 * The kind is offered as radio buttons and not as a dropdown, deliberately: 09 §3.4 puts every
 * dropdown control in the context bar, and `shell/contextSingleSource.test.ts` enforces that against
 * the source tree so a module can never grow its own picker. (That test scans for the tag name
 * without stripping comments, which is why this paragraph describes the rule instead of quoting it —
 * a rule that cannot be written down beside the code it governs is a rule that gets forgotten, so it
 * is written down, in words the scanner does not mistake for a violation.)
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { CapacityRefusalNotice } from '../components/CapacityPanel';
import { DenialNotice } from '../components/DenialNotice';
import { ErrorState, StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ApiError, DeniedError } from '../api/client';
import { callOperation } from '../api/operations';
import type { DenyBody } from '../api/contract';
import { fixtureCompanyCapacityRefusal, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';

const TITLE_HE = 'הקמת חברה';

/** 02 §5.2 — the six entity kinds, verbatim, in office Hebrew. */
const ENTITY_KINDS: readonly { readonly code: string; readonly labelHe: string }[] = [
  { code: 'company_ltd', labelHe: 'חברה בע״מ' },
  { code: 'licensed_dealer', labelHe: 'עוסק מורשה' },
  { code: 'exempt_dealer', labelHe: 'עוסק פטור' },
  { code: 'individual', labelHe: 'יחיד' },
  { code: 'ngo', labelHe: 'עמותה' },
  { code: 'reporting_entity', labelHe: 'ישות מדווחת' },
];

type Outcome =
  | { readonly kind: 'created'; readonly companyUid: string }
  | { readonly kind: 'denied'; readonly body: DenyBody }
  | { readonly kind: 'error'; readonly requestUid: string }
  | { readonly kind: 'unreachable' };

export function TenantCompanyOnboardingScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [entityKind, setEntityKind] = useState('company_ltd');
  const [taxId, setTaxId] = useState('');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  /** The server's refusal, if the office is already at its purchased limit. */
  const refusal = fixturesEnabled() ? fixtureCompanyCapacityRefusal : null;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      const created = await callOperation('companies.company.create', {
        body: {
          legalName,
          displayName,
          entityKind,
          taxId: taxId.trim() === '' ? null : taxId.trim(),
        },
      });
      setOutcome({ kind: 'created', companyUid: created.companyUid });
    } catch (err: unknown) {
      if (err instanceof DeniedError) {
        setOutcome({ kind: 'denied', body: err.body });
      } else if (err instanceof ApiError && err.status === 0) {
        setOutcome({ kind: 'unreachable' });
      } else if (err instanceof ApiError) {
        setOutcome({ kind: 'error', requestUid: err.requestUid });
      } else {
        setOutcome({ kind: 'unreachable' });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'חברות במשרד', href: routes.tenantCompanies }}
      provenanceHe="חברה חדשה נפתחת במצב ״לקוח פוטנציאלי״. היא אינה נספרת כחברה פעילה עד שמצבה משתנה במפורש."
    >
      {refusal === null ? null : <CapacityRefusalNotice refusal={refusal} />}

      <form className="form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">השם המשפטי של החברה</span>
          <input
            className="field__input"
            value={legalName}
            onChange={(e) => setLegalName(e.currentTarget.value)}
            required
          />
          <span className="field__hint">
            השם המלא כפי שהוא רשום. הוא שמזהה את החברה בכל מסך במערכת.
          </span>
        </label>

        <label className="field">
          <span className="field__label">שם לתצוגה</span>
          <input
            className="field__input"
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
          />
        </label>

        <fieldset className="field field--choice">
          <legend className="field__label">סוג הישות</legend>
          {ENTITY_KINDS.map((k) => (
            <label key={k.code} className="choice">
              <input
                type="radio"
                name="entityKind"
                value={k.code}
                checked={entityKind === k.code}
                onChange={() => setEntityKind(k.code)}
              />
              <span>{k.labelHe}</span>
            </label>
          ))}
          <span className="field__hint">סוג הישות נקבע במפורש ואינו נגזר מהשם.</span>
        </fieldset>

        <label className="field">
          <span className="field__label">מספר מזהה (ח.פ / ע.מ)</span>
          <input
            className="field__input"
            dir="ltr"
            inputMode="numeric"
            value={taxId}
            onChange={(e) => setTaxId(e.currentTarget.value)}
          />
          <span className="field__hint">
            אפשר להשאיר ריק. חברה ללא מספר מזהה תופיע ברשימה ותסומן כדורשת השלמה — היא לעולם אינה
            נעלמת מהרשימה.
          </span>
        </label>

        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? 'שולח…' : 'הקמת החברה'}
          </button>
          <a className="btn" href={routes.tenantCompanies}>
            ביטול
          </a>
        </div>
      </form>

      {outcome === null ? null : outcome.kind === 'created' ? (
        <StateBlock
          titleHe="החברה הוקמה"
          bodyHe="החברה נפתחה במצב ״לקוח פוטנציאלי״. עדיין לא ניתנה גישה אליה לאף משתמש."
          nextStep={
            <a className="btn btn--primary" href={routes.tenantCompanyAccess}>
              הענקת גישה לחברה
            </a>
          }
        />
      ) : outcome.kind === 'denied' ? (
        <DenialNotice deny={outcome.body} />
      ) : outcome.kind === 'error' ? (
        <ErrorState requestUid={outcome.requestUid} />
      ) : (
        <StateBlock
          titleHe="החברה לא הוקמה"
          bodyHe="לא הצלחנו ליצור קשר עם השרת, ולכן לא נוצרה חברה."
          tone="error"
          nextStep={<p>אפשר לנסות שוב מאוחר יותר. לא נשמר דבר.</p>}
        />
      )}
    </AppShell>
  );
}
